import { useState } from 'react';
import { useCrudPermissions } from '@/hooks/useCrudPermissions';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import type { ColumnDef } from '@tanstack/react-table';
import { CrudTable } from '@/components/CrudTable';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { useToast } from '@/hooks/use-toast';
import { useClients } from '@/hooks/useClient';
import {
  useClientContacts,
  useCreateClientContact,
  useUpdateClientContact,
  useDeleteClientContact,
} from '@/hooks/useClient';
import type { IClientContact } from '@/api/client.api';
import { getApiErrorMessage, buildErrorToast } from '@/lib/apiError';
import { EntitySelect } from '@/components/entity/EntitySelect';
import { optionalEmail, optionalText, requiredName } from '@/lib/validation';
import { INPUT_LENGTH } from '@shared/constants/input-length';

const schema = z.object({
  firstName: requiredName('First name'),
  surname: requiredName('Surname'),
  emailAddress: optionalEmail('Email address'),
  mobileCountryCode: optionalText('Mobile country code'),
  mobileNumber: optionalText('Mobile number'),
  landlineCountryCode: optionalText('Landline country code'),
  landlineAreaCode: optionalText('Landline area code'),
  landlineNumber: optionalText('Landline number'),
  streetAddress: optionalText('Street address'),
  suburb: optionalText('Suburb'),
  city: optionalText('City'),
  state: optionalText('State'),
  country: optionalText('Country'),
  postcodeZipcode: optionalText('Postcode/zip code'),
  isDailyReportRecipient: z.boolean().default(false),
  accessToViewVibe365List: z.boolean().default(false),
  accessToViewStaffShiftDetails: z.boolean().default(false),
});

type FormValues = z.infer<typeof schema>;

const columns: ColumnDef<IClientContact>[] = [
  {
    id: 'name',
    header: 'Name',
    size: 25,
    accessorFn: (c) => `${c.firstName} ${c.surname}`,
    cell: ({ row }) => `${row.original.firstName} ${row.original.surname}`,
  },
  {
    accessorKey: 'emailAddress',
    header: 'Email',
    size: 28,
    cell: ({ row }) => row.original.emailAddress
      ? <span className="break-all text-xs">{row.original.emailAddress}</span>
      : '—',
  },
  {
    id: 'mobile',
    header: 'Mobile',
    size: 17,
    accessorFn: (c) => [c.mobileCountryCode, c.mobileNumber].filter(Boolean).join(' '),
    cell: ({ row }) => {
      const { mobileCountryCode, mobileNumber } = row.original;
      if (!mobileNumber) return '—';
      return mobileCountryCode ? `${mobileCountryCode} ${mobileNumber}` : mobileNumber;
    },
  },
  {
    id: 'location',
    header: 'City / Country',
    size: 20,
    accessorFn: (c) => [c.city, c.country].filter(Boolean).join(', '),
    cell: ({ row }) => {
      const parts = [row.original.city, row.original.country].filter(Boolean);
      return parts.length ? parts.join(', ') : '—';
    },
  },
];

export function ClientContactsPage() {
  const [selectedClientId, setSelectedClientId] = useState<string>('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<IClientContact | null>(null);

  const { toast } = useToast();
  const errToast = buildErrorToast(toast);
  const { data: clientsData } = useClients({ limit: 100 });
  const { data: contacts = [], isLoading } = useClientContacts(selectedClientId || '');

  const perm = useCrudPermissions('C01');
  const createMutation = useCreateClientContact(selectedClientId);
  const updateMutation = useUpdateClientContact(selectedClientId);
  const deleteMutation = useDeleteClientContact(selectedClientId);

  const {
    register,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
  });

  const watchedCountry = watch('country');
  const watchedDailyReport = watch('isDailyReportRecipient');
  const watchedVibe365 = watch('accessToViewVibe365List');
  const watchedShiftDetails = watch('accessToViewStaffShiftDetails');

  function handleAdd() {
    setEditTarget(null);
    reset({});
    setDialogOpen(true);
  }

  function handleEdit(row: IClientContact) {
    setEditTarget(row);
    reset({
      firstName: row.firstName,
      surname: row.surname,
      emailAddress: row.emailAddress ?? '',
      mobileCountryCode: row.mobileCountryCode ?? '',
      mobileNumber: row.mobileNumber ?? '',
      landlineCountryCode: row.landlineCountryCode ?? '',
      landlineAreaCode: row.landlineAreaCode ?? '',
      landlineNumber: row.landlineNumber ?? '',
      streetAddress: row.streetAddress ?? '',
      suburb: row.suburb ?? '',
      city: row.city ?? '',
      state: row.state ?? '',
      country: row.country ?? '',
      postcodeZipcode: row.postcodeZipcode ?? '',
      isDailyReportRecipient: row.isDailyReportRecipient,
      accessToViewVibe365List: row.accessToViewVibe365List,
      accessToViewStaffShiftDetails: row.accessToViewStaffShiftDetails,
    });
    setDialogOpen(true);
  }

  async function handleDelete(row: IClientContact) {
    try {
      await deleteMutation.mutateAsync(row.id);
      toast({ title: 'Contact deleted' });
    } catch (err: unknown) {
      toast({ title: getApiErrorMessage(err, 'Failed to delete contact'), variant: 'destructive' });
    }
  }

  async function onSubmit(values: FormValues) {
    const dto = Object.fromEntries(
      Object.entries(values).map(([k, v]) => [k, v === '' ? undefined : v]),
    ) as FormValues;

    try {
      if (editTarget) {
        await updateMutation.mutateAsync({ contactId: editTarget.id, dto });
        toast({ title: 'Contact updated' });
      } else {
        await createMutation.mutateAsync(dto);
        toast({ title: 'Contact created' });
      }
      setDialogOpen(false);
    } catch (err: unknown) {
      errToast(err, editTarget ? 'Failed to update contact' : 'Failed to create contact');
    }
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold">Client Contacts</h1>
        <p className="text-sm text-muted-foreground">Manage contacts for each client</p>
      </div>

      <div className="space-y-1 max-w-sm">
        <Label htmlFor="clientSelect">Client</Label>
        <EntitySelect
          value={selectedClientId}
          onValueChange={setSelectedClientId}
          options={clientsData?.data ?? []}
          placeholder="Select client"
          emptyLabel="Select client"
          entityType="CLIENT"
          testId="client-contacts-client-select"
          triggerClassName="w-full"
        />
      </div>

      {selectedClientId ? (
        <CrudTable
          columns={columns}
          data={contacts}
          isLoading={isLoading}
          onAdd={perm.canCreate ? handleAdd : undefined}
          onEdit={perm.canUpdate ? handleEdit : undefined}
          onDelete={perm.canDelete ? handleDelete : undefined}
          addLabel="Add Contact"
        />
      ) : (
        <p className="text-sm text-muted-foreground">Select a client above to view its contacts.</p>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>{editTarget ? 'Edit Contact' : 'Add Contact'}</DialogTitle>
          </DialogHeader>

          <form id="client-contacts-form" onSubmit={handleSubmit(onSubmit)} className="flex-1 min-h-0 overflow-y-auto px-6 py-4 space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label htmlFor="firstName">First Name *</Label>
                <Input id="firstName" maxLength={INPUT_LENGTH.name} {...register('firstName')} />
                {errors.firstName && <p className="text-xs text-destructive">{errors.firstName.message}</p>}
              </div>
              <div className="space-y-1">
                <Label htmlFor="surname">Surname *</Label>
                <Input id="surname" maxLength={INPUT_LENGTH.name} {...register('surname')} />
                {errors.surname && <p className="text-xs text-destructive">{errors.surname.message}</p>}
              </div>
            </div>

            <div className="space-y-1">
              <Label htmlFor="emailAddress">Email</Label>
              <Input id="emailAddress" type="email" maxLength={INPUT_LENGTH.text} {...register('emailAddress')} />
              {errors.emailAddress && <p className="text-xs text-destructive">{errors.emailAddress.message}</p>}
            </div>

            <div className="grid grid-cols-3 gap-2">
              <div className="space-y-1">
                <Label htmlFor="mobileCountryCode">Country Code</Label>
                <Input id="mobileCountryCode" maxLength={INPUT_LENGTH.text} placeholder="+61" {...register('mobileCountryCode')} />
              </div>
              <div className="col-span-2 space-y-1">
                <Label htmlFor="mobileNumber">Mobile Number</Label>
                <Input id="mobileNumber" maxLength={INPUT_LENGTH.text} {...register('mobileNumber')} />
              </div>
            </div>

            <div className="space-y-1">
              <Label>Landline</Label>
              <div className="grid grid-cols-4 gap-2">
                <Input placeholder="Country" maxLength={INPUT_LENGTH.text} {...register('landlineCountryCode')} />
                <Input placeholder="Area" maxLength={INPUT_LENGTH.text} {...register('landlineAreaCode')} />
                <Input placeholder="Number" className="col-span-2" maxLength={INPUT_LENGTH.text} {...register('landlineNumber')} />
              </div>
            </div>

            <div className="border-t pt-4">
              <p className="text-sm font-medium text-muted-foreground mb-3">Address</p>
              <div className="space-y-3">
                <div className="space-y-1">
                  <Label htmlFor="streetAddress">Street Address</Label>
                  <Input id="streetAddress" maxLength={INPUT_LENGTH.text} {...register('streetAddress')} />
                </div>
                <div className="space-y-1">
                  <Label htmlFor="suburb">Suburb</Label>
                  <Input id="suburb" maxLength={INPUT_LENGTH.text} {...register('suburb')} />
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div className="space-y-1">
                    <Label htmlFor="city">City</Label>
                    <Input id="city" maxLength={INPUT_LENGTH.text} {...register('city')} />
                  </div>
                  <div className="space-y-1">
                    <Label htmlFor="state">State</Label>
                    <Input id="state" maxLength={INPUT_LENGTH.text} {...register('state')} />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div className="space-y-1">
                    <Label htmlFor="country">Country</Label>
                    <Input id="country" maxLength={INPUT_LENGTH.text} {...register('country')} />
                  </div>
                  <div className="space-y-1">
                    <Label htmlFor="postcodeZipcode">
                      {watchedCountry?.toUpperCase() === 'US' ? 'ZIP Code' : 'Postcode'}
                    </Label>
                    <Input id="postcodeZipcode" maxLength={INPUT_LENGTH.text} {...register('postcodeZipcode')} />
                  </div>
                </div>
              </div>
            </div>
            <div className="border-t pt-4 space-y-3">
              <p className="text-sm font-medium text-muted-foreground">Access &amp; Notifications</p>
              <div className="flex items-center justify-between">
                <div>
                  <Label>Include on Staff Attendance Email</Label>
                  <p className="text-xs text-muted-foreground">Contact will be included in daily attendance report emails</p>
                </div>
                <Switch checked={watchedDailyReport} onCheckedChange={(v) => setValue('isDailyReportRecipient', v)} />
              </div>
              <div className="flex items-center justify-between">
                <Label>Access to View VIBE365 List</Label>
                <Switch checked={watchedVibe365} onCheckedChange={(v) => setValue('accessToViewVibe365List', v)} />
              </div>
              <div className="flex items-center justify-between">
                <Label>Access to View Staff Shift Details</Label>
                <Switch checked={watchedShiftDetails} onCheckedChange={(v) => setValue('accessToViewStaffShiftDetails', v)} />
              </div>
            </div>

          </form>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
              Cancel
            </Button>
            <Button type="submit" form="client-contacts-form" disabled={isSubmitting}>
              {isSubmitting ? 'Saving...' : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
